import { StateCreator } from 'zustand';
import { GameState } from '../gameStore';
import { getHighScores, submitHighScore } from '../../firebase';
import { getModelMultiplier } from '../../constants/modelConstants';

export interface HighScore {
  name: string;
  score: number;
  model: string;
  date: string;
  visionEnabled: boolean; // Track if vision was used for the high score
}

export interface ScoreSlice {
  currentScore: number;
  highScores: HighScore[];
  showHighScoreEntry: boolean;
  highScoreSubmitted: boolean;
  isLoadingHighScores: boolean;
  
  calculateScore: () => number;
  checkHighScore: (score: number) => boolean;
  addHighScore: (name: string) => Promise<void>;
  setShowHighScoreEntry: (show: boolean) => void;
  loadHighScores: () => Promise<void>;
}

export const createScoreSlice: StateCreator<
  GameState,
  [],
  [],
  ScoreSlice
> = (set, get) => ({
  currentScore: 0,
  highScores: [],
  showHighScoreEntry: false,
  highScoreSubmitted: false,
  isLoadingHighScores: false,
  
  calculateScore: () => {
    const { playerTimeRemaining, selectedModel, useVision, boardOrientation, gameTimeLimit } = get();
    const color = boardOrientation === 'black' ? 'black' : 'white';
    const victoryBonus = 500;
    const timeMultiplier = 12 / gameTimeLimit;
    
    // Use the centralized model multiplier from our constants
    const modelMultiplier = getModelMultiplier(selectedModel, useVision);
    
    const baseScore = victoryBonus + (playerTimeRemaining * timeMultiplier);
    const multipliedScore = Math.round(baseScore * modelMultiplier);

    const finalScore = Math.round(color === 'black' ? multipliedScore * 1.04 : multipliedScore);
    
    set({ currentScore: finalScore });
    return finalScore;
  },
  
  checkHighScore: (score) => {
    const { highScores } = get();
    
    if (highScores.length < 10) {
      return true;
    }
    
    const lowestHighScore = [...highScores].sort((a, b) => a.score - b.score)[0];
    return score > lowestHighScore.score;
  },
  
  addHighScore: async (name) => {
    const { currentScore, selectedModel, useVision } = get();
    set({ showHighScoreEntry: false, isLoadingHighScores: true });
    
    try {
      // Submit high score with vision information
      const success = await submitHighScore(
        name, 
        currentScore, 
        selectedModel,
        useVision
      );
      
      if (success) {
        // Reload high scores after successful submission
        await get().loadHighScores();
        set({ highScoreSubmitted: true });
      } else {
        console.error('Failed to submit high score');
      }
    } catch (error) {
      console.error('Error in addHighScore:', error);
    } finally {
      set({ isLoadingHighScores: false, showHighScoreEntry: false });
    }
  },
  
  loadHighScores: async () => {
    set({ isLoadingHighScores: true });
    
    try {
      // Use the server function to get high scores
      const highScores = await getHighScores();
      set({ highScores });
    } catch (error) {
      console.error('Error in loadHighScores:', error);
    } finally {
      set({ isLoadingHighScores: false });
    }
  },
  
  setShowHighScoreEntry: (show) => set({ showHighScoreEntry: show }),
});